import React, { useState, useEffect } from "react";
import { db } from "@/api/db";
import { createPageUrl } from "@/utils";
import { Link } from "react-router-dom";
import { FlaskConical, ArrowLeft, CheckCircle, Clock, Send, Star } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export default function ProjectDetail() {
  const params = new URLSearchParams(window.location.search);
  const projectId = params.get("id");

  const [project, setProject] = useState(null);
  const [user, setUser] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [evaluation, setEvaluation] = useState(null);
  const [submissionUrl, setSubmissionUrl] = useState("");
  const [submissionText, setSubmissionText] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    const u = await db.auth.me();
    setUser(u);
    const [projects, subs] = await Promise.all([
      db.entities.PracticalProject.filter({ id: projectId }),
      db.entities.ProjectSubmission.filter({ project_id: projectId, user_email: u.email }),
    ]);
    const p = projects[0];
    setProject(p);
    const sub = subs[0] || null;
    setSubmission(sub);
    if (sub?.status === "evaluated") {
      const evals = await db.entities.ProjectEvaluation.filter({ submission_id: sub.id });
      setEvaluation(evals[0] || null);
    }
    setLoading(false);
  }

  async function handleSubmit() {
    if (!submissionUrl && !submissionText) return;
    setSubmitting(true);
    await db.entities.ProjectSubmission.create({
      project_id: projectId,
      user_email: user.email,
      submission_url: submissionUrl,
      submission_text: submissionText,
      status: "pending",
    });
    setSubmission({ status: "pending" });
    setSubmitting(false);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: '#00DCA4', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (!project) {
    return <div className="text-center py-20" style={{ color: '#99A6AB' }}>Projeto não encontrado.</div>;
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <Link to={createPageUrl("Tracks")} className="inline-flex items-center gap-1.5 text-sm"
        style={{ color: '#646C6F' }}>
        <ArrowLeft className="w-4 h-4" /> Voltar
      </Link>

      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
          style={{ background: '#EAFAF6' }}>
          <FlaskConical className="w-6 h-6" style={{ color: '#00664C' }} />
        </div>
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Poppins, sans-serif', color: '#2E3233' }}>
              {project.title}
            </h1>
            {project.difficulty && (
              <Badge className="border-0 text-xs" style={{ background: '#B0FCEA', color: '#003326' }}>
                {project.difficulty}
              </Badge>
            )}
          </div>
          <p className="mt-1" style={{ color: '#646C6F' }}>{project.description}</p>
        </div>
      </div>

      {/* Briefing */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6 space-y-4">
          <h2 className="font-semibold" style={{ color: '#2E3233' }}>📋 Briefing do Desafio</h2>
          <p className="whitespace-pre-line leading-relaxed" style={{ color: '#646C6F' }}>
            {project.briefing || "Briefing não disponível."}
          </p>
          {project.deliverables?.length > 0 && (
            <div>
              <p className="font-medium mb-2" style={{ color: '#2E3233' }}>Entregáveis esperados:</p>
              <ul className="space-y-1.5">
                {project.deliverables.map((d, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm" style={{ color: '#646C6F' }}>
                    <span className="w-5 h-5 rounded-full text-xs flex items-center justify-center flex-shrink-0 mt-0.5 font-bold"
                      style={{ background: '#EAFAF6', color: '#00664C' }}>{i + 1}</span>
                    {d}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {project.skills_tested?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-2">
              {project.skills_tested.map(s => (
                <span key={s} className="text-xs px-2.5 py-1 rounded-full"
                  style={{ background: '#F2F4F4', color: '#646C6F' }}>{s}</span>
              ))}
            </div>
          )}
          <div className="flex items-center gap-1 text-sm font-medium pt-1" style={{ color: '#00664C' }}>
            <Star className="w-4 h-4" style={{ fill: '#00DCA4', color: '#00DCA4' }} />
            {project.points} pontos ao ser avaliado
          </div>
        </CardContent>
      </Card>

      {/* Evaluation result */}
      {evaluation && (
        <Card className="border-0 shadow-sm" style={{ borderLeft: '4px solid #00DCA4' }}>
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6" style={{ color: '#00DCA4' }} />
              <div>
                <h2 className="font-semibold" style={{ color: '#2E3233' }}>Avaliação recebida</h2>
                <p className="text-sm" style={{ color: '#646C6F' }}>
                  Nota: <span className="font-bold" style={{ color: '#00664C' }}>{evaluation.score}/100</span>
                </p>
              </div>
            </div>
            {evaluation.feedback && (
              <p className="text-sm leading-relaxed" style={{ color: '#646C6F' }}>{evaluation.feedback}</p>
            )}
            {evaluation.strengths?.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-1" style={{ color: '#2E3233' }}>✅ Pontos fortes:</p>
                <ul className="space-y-1">
                  {evaluation.strengths.map((s, i) => (
                    <li key={i} className="text-sm" style={{ color: '#646C6F' }}>• {s}</li>
                  ))}
                </ul>
              </div>
            )}
            {evaluation.improvements?.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-1" style={{ color: '#2E3233' }}>📈 A melhorar:</p>
                <ul className="space-y-1">
                  {evaluation.improvements.map((s, i) => (
                    <li key={i} className="text-sm" style={{ color: '#646C6F' }}>• {s}</li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Submission pending */}
      {submission?.status === "pending" && !evaluation && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6 flex items-center gap-3">
            <Clock className="w-6 h-6 flex-shrink-0" style={{ color: '#B2911E' }} />
            <div>
              <p className="font-medium" style={{ color: '#2E3233' }}>Entrega recebida!</p>
              <p className="text-sm" style={{ color: '#646C6F' }}>
                Seu projeto está aguardando avaliação. Você receberá feedback em breve.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Submission form */}
      {!submission && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6 space-y-4">
            <h2 className="font-semibold" style={{ color: '#2E3233' }}>📤 Enviar entrega</h2>
            <div>
              <label className="text-sm font-semibold block mb-1.5" style={{ color: '#003326' }}>
                Link do entregável (Google Drive, Notion, GitHub...)
              </label>
              <input
                type="url"
                value={submissionUrl}
                onChange={e => setSubmissionUrl(e.target.value)}
                placeholder="https://..."
                className="w-full px-3 py-2.5 rounded-xl text-sm focus:outline-none"
                style={{ border: '1px solid #E5E9EA', color: '#2E3233', background: '#fff' }}
                onFocus={e => e.target.style.borderColor = '#00DCA4'}
                onBlur={e => e.target.style.borderColor = '#E5E9EA'}
              />
            </div>
            <div>
              <label className="text-sm font-semibold block mb-1.5" style={{ color: '#003326' }}>
                Descrição / comentários (opcional)
              </label>
              <textarea
                value={submissionText}
                onChange={e => setSubmissionText(e.target.value)}
                rows={4}
                placeholder="Explique o que você fez, decisões tomadas, aprendizados..."
                className="w-full px-3 py-2.5 rounded-xl text-sm focus:outline-none resize-none"
                style={{ border: '1px solid #E5E9EA', color: '#2E3233', background: '#fff' }}
                onFocus={e => e.target.style.borderColor = '#00DCA4'}
                onBlur={e => e.target.style.borderColor = '#E5E9EA'}
              />
            </div>
            <button
              onClick={handleSubmit}
              disabled={submitting || (!submissionUrl && !submissionText)}
              className="flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold transition-opacity"
              style={submitting || (!submissionUrl && !submissionText)
                ? { background: '#E5E9EA', color: '#99A6AB' }
                : { background: '#00DCA4', color: '#003326' }}
            >
              {submitting ? "Enviando..." : <><Send className="w-4 h-4" /> Enviar entrega</>}
            </button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
